import React, { useState, useEffect } from 'react';
import contextAnalyticsService from '../../../services/contextAnalyticsService';
import {
  DocumentTextIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const KnowledgeIndexingQueue = ({ showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [recentIndexes, setRecentIndexes] = useState([]);
  const [retrying, setRetrying] = useState({});

  useEffect(() => {
    fetchIndexingStatus();
  }, []);

  const fetchIndexingStatus = async () => {
    try {
      setLoading(true);

      const [statusResponse, recentResponse] = await Promise.all([
        contextAnalyticsService.getKnowledgeIndexingStatus(),
        contextAnalyticsService.getRecentIndexes(10)
      ]);

      if (statusResponse.success) {
        setStatus(statusResponse.data);
      }

      if (recentResponse.success) {
        setRecentIndexes(recentResponse.data);
      }
    } catch (error) {
      logger.error('Error fetching indexing status:', error);
      showNotification?.('Failed to load indexing status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryIndexing = async (knowledgeId) => {
    try {
      setRetrying({ ...retrying, [knowledgeId]: true });

      const response = await contextAnalyticsService.retryIndexing(knowledgeId);

      if (response.success) {
        showNotification?.('Indexing job queued successfully', 'success');
        fetchIndexingStatus();
      }
    } catch (error) {
      logger.error('Error retrying indexing:', error);
      showNotification?.('Failed to retry indexing', 'error');
    } finally {
      setRetrying({ ...retrying, [knowledgeId]: false });
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Knowledge Indexing Status
            </h3>
          </div>
          <button
            onClick={fetchIndexingStatus}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Status Summary */}
        {status && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                <span className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {status.summary?.synced || 0}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Synced</p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <ArrowPathIcon className="h-8 w-8 text-yellow-600" />
                <span className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                  {status.summary?.pending || 0}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Pending</p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" />
                <span className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {status.summary?.syncing || 0}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Syncing</p>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <ExclamationCircleIcon className="h-8 w-8 text-red-600" />
                <span className="text-2xl font-bold text-red-900 dark:text-red-100">
                  {status.summary?.failed || 0}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Failed</p>
            </div>
          </div>
        )}

        {/* Failed Jobs */}
        {status?.failedJobs && status.failedJobs.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Failed Indexing Jobs
            </h4>
            <div className="space-y-2">
              {status.failedJobs.map((job) => (
                <div
                  key={job._id}
                  className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{job.title}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Type: {job.contentType} | Updated: {new Date(job.updatedAt).toLocaleString()}
                      </p>
                      {job.vectorStore?.lastError && (
                        <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                          Error: {job.vectorStore.lastError}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRetryIndexing(job._id)}
                      disabled={retrying[job._id]}
                      className="ml-4 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {retrying[job._id] ? (
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      ) : (
                        'Retry'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recently Indexed */}
        {recentIndexes && recentIndexes.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Recently Indexed Documents
            </h4>
            <div className="space-y-2">
              {recentIndexes.map((doc) => (
                <div
                  key={doc._id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#222222] rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{doc.title}</p>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                      <span>Type: {doc.contentType}</span>
                      <span>•</span>
                      <span>Chunks: {doc.vectorStore?.chunkCount || 0}</span>
                      <span>•</span>
                      <span>
                        Departments: {doc.departments?.join(', ') || 'None'}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(doc.vectorStore?.indexedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeIndexingQueue;
